import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ER_STAKEHOLDER_TYPES,
  COMMUNICATIONS_ROLE_OPTIONS,
  JOB_TITLE_OPTIONS,
  requiresCommunicationsRoleJobTitle,
  STAKEHOLDER_OPTIONS,
  buildAccountSetupCrmPayload,
  defaultJurisdictionRow,
  firstProfileStepId,
  getVisibleRegistrationSteps,
  isLastRegistrationStep,
  listRegistrationCountiesForState,
  listRegistrationCountryNames,
  listRegistrationStatesForCountry,
  nextRegistrationStep,
  parseRegistrationUrlParams,
  prepopulateFirstJurisdictionFromMailing,
  previousRegistrationStep,
  registrationAccountResumeSchema,
  registrationAccountSchema,
  registrationAgencyCapabilitiesStepSchema,
  registrationAgencyInfoStepSchema,
  registrationAgencyProceduresStepSchema,
  registrationAgencyTrainingStepSchema,
  DISPATCH_CAPABILITIES_PART_COUNT,
  firstInvalidDispatchCapabilitiesPart,
  registrationDispatchCapabilitiesPartSchema,
  registrationDispatchCapabilitiesStepSchema,
  type DispatchCapabilitiesPart,
  registrationExtendedDefaults,
  registrationJurisdictionStepSchema,
  registrationMailingStepSchema,
  registrationMutualAidStepSchema,
  registrationAdditionalQuestionsStepSchema,
  registrationOrganizationStepSchema,
  registrationOutcomeFromResolvedStatus,
  registrationSuccessParagraphs,
  REGISTRATION_FAILURE_MESSAGE,
  PUBLIC_OFFICIAL_ORG_TYPES,
  SCHOOL_REP_ORG_TYPES,
  resolveRegistrationStakeholderType,
  resolveRegistrationWizardStakeholderType,
  showOrganizationDepartmentField,
  showOrganizationTypeField,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  STAKEHOLDER_SCHOOL_REP,
  toRegisterBuxusRequest,
  QUICK_CODE_INPUT_PLACEHOLDER,
  formatUsPhoneTyping,
  type RegistrationAccountResumeValues,
  type RegistrationAccountValues,
  type RegistrationExtendedFormValues,
  type RegistrationJurisdictionRow,
  type RegistrationStepContext,
  type RegistrationStepId,
} from "@buxus/shared";
import {
  getContactByQuickCodeComputed,
  parseAccountSetupCrmResponse,
} from "./registrationApi";
import { friendlyRegistrationError } from "./registrationErrors";
import {
  accountSetupCrmTracked,
  findUserByEmailInBuxusTracked,
  flattenZodFieldErrors,
  registerBuxusAccountTracked,
  registrationFlowContextFromUrl,
  trackRegistrationFormValidationFailed,
  trackRegistrationPageViewed,
  trackRegistrationErrorOccurred,
  trackRegistrationQuickCodeFetched,
  trackRegistrationStepChanged,
  type RegistrationStepAnalyticsContext,
} from "../platform/analytics/registrationAnalytics";
import { reportRegistrationFailureIfServer } from "../platform/errors/webFailureReporting";
import {
  mapQuickCodeContactToForms,
  mergeAccountFromQuickCode,
  mergeExtendedFromQuickCode,
  parseQuickCodeLookupResponse,
} from "./quickCodeContactMap";
import {
  formatQuickCodeInput,
  hasQuickCodeInput,
  quickCodeForLookup,
} from "./quickCodeFormat";
import {
  hasRegistrationNativeBridge,
  leaveRegistrationHost,
  postDisplayErrorModal,
  postRegistrationCompleteToLogin,
} from "./registrationHostBridge";
import { showQuickCodeLookupErrorModal } from "./showQuickCodeLookupErrorModal";
import { useAbp } from "../platform/abp/react";
import { RegPasswordField } from "../ui/RegPasswordField";
import { RegReqLabel } from "../ui/RegReqLabel";
import {
  engineStepToUiStep,
  uiStepToEngineStep,
  type RegistrationUiStep,
} from "./stepNavigation";
import { getRegistrationDevJumpBootstrap } from "./registrationDevJump";
import { AgencyCapabilitiesStep } from "./steps/AgencyCapabilitiesStep";import { AgencyInfoStep } from "./steps/AgencyInfoStep";
import { AgencyProceduresStep } from "./steps/AgencyProceduresStep";
import { AgencyTrainingStep } from "./steps/AgencyTrainingStep";
import { DispatchCapabilitiesStep } from "./steps/DispatchCapabilitiesStep";
import { MutualAidStep } from "./steps/MutualAidStep";
import { AdditionalQuestionsStep } from "./steps/AdditionalQuestionsStep";
import {
  detectRegistrationHostKind,
  RegistrationShell,
  useRegistrationScrollToTop,
  type RegistrationHostKind,
} from "./registrationShell";
import "./RegistrationFlow.css";

const REGISTRATION_COUNTRIES = listRegistrationCountryNames();

type Step = RegistrationUiStep;

type JurisdictionFieldErrors = Partial<Record<keyof RegistrationJurisdictionRow, string>>;

/** Legacy `registrationStore.operatorType` defaults to Emergency Responder; QC `mapQuickCodeData` overwrites from CRM when present. */
const DEFAULT_VALUES: RegistrationAccountValues = {
  stakeholderType: "ER - Emergency Responder",
  jobTitle: "",
  isUnaffiliatedProfessionalErTrainer: "",
  communicationsRole: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  password: "",
  passwordConfirm: "",
};

export function RegistrationFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useMemo(() => parseRegistrationUrlParams(location.search), [location.search]);
  const { isAuthenticated, logout } = useAbp();
  const [hostKind] = useState<RegistrationHostKind>(detectRegistrationHostKind);

  // DEV-only: see registrationDevJump.ts — set REGISTRATION_DEV_JUMP.startStep to jump ahead.
  const [devJump] = useState(() => getRegistrationDevJumpBootstrap());

  const [step, setStep] = useState<Step>(() => devJump?.step ?? "quickCode");
  const [hasQuickCode, setHasQuickCode] = useState<boolean | null>(() =>
    devJump ? devJump.hasQuickCode : null,
  );
  const [quickCode, setQuickCode] = useState("");
  const [form, setForm] = useState<RegistrationAccountValues>(() => devJump?.form ?? DEFAULT_VALUES);
  const [extendedForm, setExtendedForm] = useState<RegistrationExtendedFormValues>(
    () => devJump?.extendedForm ?? registrationExtendedDefaults(),
  );
  const [accountBlurred, setAccountBlurred] = useState<Partial<Record<keyof RegistrationAccountValues, true>>>({});  const [accountSubmitAttempted, setAccountSubmitAttempted] = useState(false);
  const [jurisdictionCellBlurred, setJurisdictionCellBlurred] = useState<Partial<Record<string, true>>>({});
  const [jurisdictionSubmitAttempted, setJurisdictionSubmitAttempted] = useState(false);
  const [mailingBlurred, setMailingBlurred] = useState<
    Partial<
      Record<
        "mailingAddress1" | "mailingCity" | "mailingCountry" | "mailingState" | "mailingCounty" | "mailingZip",
        true
      >
    >
  >({});
  const [mailingSubmitAttempted, setMailingSubmitAttempted] = useState(false);
  const [orgBlurred, setOrgBlurred] = useState<
    Partial<Record<"institution" | "organizationType" | "department" | "institutionPhone", true>>
  >({});
  const [orgSubmitAttempted, setOrgSubmitAttempted] = useState(false);
  const [agencyBlurred, setAgencyBlurred] = useState<
    Partial<
      Record<
        | "typeOfAgency"
        | "nameOfAgencyCommandingOfficer"
        | "nonEmergencyNumberForAgencyOrDepartment"
        | "website"
        | "staffed"
        | "numberOfPeopleInDepartment"
        | "dispatchRadioSystemsUsedByAgency"
        | "dispatchRadioSystemsUsedByAgencyOther"
        | "dispatchIsRadioSystemP25Compliant"
        | "dispatchIsRadioSystemEncrypted",
        true
      >
    >
  >({});
  const [agencySubmitAttempted, setAgencySubmitAttempted] = useState(false);
  const [additionalQuestionsSubmitAttempted, setAdditionalQuestionsSubmitAttempted] =
    useState(false);
  const [additionalQuestionsBlurred, setAdditionalQuestionsBlurred] = useState<
    Partial<
      Record<
        | "howDidYouFindOutAboutBuxus"
        | "howDidYouFindOutAboutBuxusAgency"
        | "howDidYouFindOutAboutBuxusAssociation"
        | "howDidYouFindOutAboutBuxusConference"
        | "howDidYouFindOutAboutBuxusOperator"
        | "howDidYouFindOutAboutBuxusTrainer"
        | "howDidYouFindOutAboutBuxusWebinar",
        true
      >
    >
  >({});
  const [dispatchSubmitAttempted, setDispatchSubmitAttempted] = useState(false);
  const [dispatchPart, setDispatchPart] = useState<DispatchCapabilitiesPart>(
    () => devJump?.dispatchPart ?? 1,
  );
  const [dispatchBlurred, setDispatchBlurred] = useState<
    Partial<
      Record<
        | "dispatchFacilityClassification"
        | "dispatchDisciplinesServedByAgency"
        | "dispatchMainAreaOfResponsibility"
        | "dispatchEstPopulationOfResponsibilityArea"
        | "dispatchComputerAidedDispatchSystemUsed"
        | "dispatchComputerAidedDispatchSystemUsedOther"
        | "dispatchCanDetermineCallerLatLongGis"
        | "dispatchProgramsUsedByAgency"
        | "dispatchRadioSystemsUsedByAgency"
        | "dispatchRadioSystemsUsedByAgencyOther"
        | "dispatchPublicNotificationSystemUsed"
        | "dispatchPublicNotificationSystemUsedOther"
        | "dispatchIsContinuingEducationRequired"
        | "dispatchDoesStateRequireCertification"
        | "dispatchDoesStateRequireCertificationOther"
        | "dispatchTrainingCoversEmergencyTopics"
        | "dispatchHasAccessToEmergencyTopics"
        | "dispatchSopSogForEmergencyTopics"
        | "dispatchCompletedCoursesOnEmergencyTopics",
        true
      >
    >
  >({});
  const [trainingSubmitAttempted, setTrainingSubmitAttempted] = useState(false);
  const [hazwoperBlurred, setHazwoperBlurred] = useState(false);
  const [basicsSubmitting, setBasicsSubmitting] = useState(false);
  const [crmSubmitting, setCrmSubmitting] = useState(false);
  const [quickCodeLookupLoading, setQuickCodeLookupLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resolvedStatus, setResolvedStatus] = useState<string | null>(null);
  const [approvalCode, setApprovalCode] = useState("");
  const [eventCodeSlug, setEventCodeSlug] = useState<string | null>(null);
  const [abpUserId, setAbpUserId] = useState<string | null>(() => devJump?.abpUserId ?? null);
  const [committedAccountEmail, setCommittedAccountEmail] = useState<string | null>(
    () => devJump?.committedAccountEmail ?? null,
  );  /** QC + ABP user exists without CRM link: hide password fields (password not applied on resume). */
  const [omitPasswordAfterQc, setOmitPasswordAfterQc] = useState(false);
  /** Email used for QC resume detection; if user edits email, re-show passwords. */
  const [qcResumeEmailSnapshot, setQcResumeEmailSnapshot] = useState<string | null>(null);
  const [quickCodeBlockedEmail, setQuickCodeBlockedEmail] = useState<string | null>(null);

  const regFlowCtx = useMemo(
    () => registrationFlowContextFromUrl(urlParams, hostKind === "webview"),
    [urlParams, hostKind],
  );

  const stepDirectionRef = useRef<"forward" | "backward" | "jump">("forward");
  const initialStepTrackedRef = useRef(false);
  const prevAnalyticsStepRef = useRef<Step | null>(null);
  const pageViewTrackedRef = useRef(false);
  const expressReturnBootstrapped = useRef(false);
  /** Invite email `?referralCode=` with embedded quickCodeComputed (parity with buxusweb isQuickCodeReferral). */
  const referralQuickCodeBootstrapped = useRef(false);

  const changeStep = useCallback((next: Step, direction: "forward" | "backward" | "jump" = "forward") => {
    stepDirectionRef.current = direction;
    setStep(next);
  }, []);

  useEffect(() => {
    if (pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    trackRegistrationPageViewed("Registration", regFlowCtx);
  }, [regFlowCtx]);

  const prevStepRef = useRef<Step | null>(null);
  const prevDispatchPartRef = useRef<DispatchCapabilitiesPart | null>(null);
  const scrollRegistrationToTop = useRegistrationScrollToTop();
  useEffect(() => {
    const prev = prevStepRef.current;
    if (prev !== null && prev !== step) {
      scrollRegistrationToTop();
    }
    if (prev !== step) {
      if (step === "accountBasics") {
        setAccountBlurred({});
        setAccountSubmitAttempted(false);
      }
      if (step === "jurisdictions") {
        setJurisdictionCellBlurred({});
        setJurisdictionSubmitAttempted(false);
      }
      if (step === "mailing") {
        setMailingBlurred({});
        setMailingSubmitAttempted(false);
      }
      if (step === "organization") {
        setOrgBlurred({});
        setOrgSubmitAttempted(false);
      }
      if (step === "agencyInfo") {
        setAgencyBlurred({});
        setAgencySubmitAttempted(false);
      }
      if (step === "dispatchCapabilities") {
        setDispatchBlurred({});
        setDispatchSubmitAttempted(false);
      }
      if (step === "agencyTraining") {
        setTrainingSubmitAttempted(false);
        setHazwoperBlurred(false);
      }
    }
    prevStepRef.current = step;
  }, [step, scrollRegistrationToTop]);

  useEffect(() => {
    if (step !== "dispatchCapabilities") {
      prevDispatchPartRef.current = null;
      return;
    }
    const prev = prevDispatchPartRef.current;
    if (prev !== null && prev !== dispatchPart) {
      scrollRegistrationToTop();
    }
    prevDispatchPartRef.current = dispatchPart;
  }, [step, dispatchPart, scrollRegistrationToTop]);

  const wizardStakeholderType = useMemo(
    () => resolveRegistrationWizardStakeholderType(form.stakeholderType, form.jobTitle),
    [form.stakeholderType, form.jobTitle],
  );

  const crmStakeholderType = useMemo(
    () =>
      resolveRegistrationStakeholderType(
        form.stakeholderType,
        form.jobTitle,
        form.isUnaffiliatedProfessionalErTrainer,
      ),
    [form.stakeholderType, form.jobTitle, form.isUnaffiliatedProfessionalErTrainer],
  );

  const stepContext = useMemo(
    (): RegistrationStepContext => ({
      stakeholderType: wizardStakeholderType,
      jobTitle: form.jobTitle,
      typeOfAgency: extendedForm.typeOfAgency,
    }),
    [wizardStakeholderType, form.jobTitle, extendedForm.typeOfAgency],
  );

  const regStepCtx = useMemo((): RegistrationStepAnalyticsContext => {
    return {
      ...regFlowCtx,
      totalSteps: getVisibleRegistrationSteps(stepContext).length,
      stakeholderType: wizardStakeholderType,
      emailAddress: form.email.trim() || undefined,
      hasQuickCode: hasQuickCode === true && !!quickCode.trim(),
      hasReferrerContactId: !!urlParams.referrerContactId,
    };
  }, [
    regFlowCtx,
    stepContext,
    wizardStakeholderType,
    form.email,
    hasQuickCode,
    quickCode,
    urlParams.referrerContactId,
  ]);

  useEffect(() => {
    if (urlParams.isExpressReturnMode && !expressReturnBootstrapped.current && step === "quickCode") {
      return;
    }
    const previous = prevAnalyticsStepRef.current;
    if (!initialStepTrackedRef.current) {
      initialStepTrackedRef.current = true;
      trackRegistrationStepChanged({
        direction: "initial",
        previousStep: null,
        currentStep: step,
        ctx: regStepCtx,
      });
      prevAnalyticsStepRef.current = step;
      return;
    }
    if (previous === step) return;
    trackRegistrationStepChanged({
      direction: stepDirectionRef.current,
      previousStep: previous,
      currentStep: step,
      ctx: regStepCtx,
    });
    prevAnalyticsStepRef.current = step;
  }, [step, regStepCtx, urlParams.isExpressReturnMode]);

  const skipsPasswordOnAccount = useMemo(
    () => (hasQuickCode === true && omitPasswordAfterQc) || urlParams.isExpressReturnMode,
    [hasQuickCode, omitPasswordAfterQc, urlParams.isExpressReturnMode],
  );

  const effectiveAccountSchema = useMemo(
    () => (skipsPasswordOnAccount ? registrationAccountResumeSchema : registrationAccountSchema),
    [skipsPasswordOnAccount],
  );

  const isErLike = useMemo(
    () => ER_STAKEHOLDER_TYPES.includes(wizardStakeholderType as (typeof ER_STAKEHOLDER_TYPES)[number]),
    [wizardStakeholderType],
  );

  const showJobTitleField = form.stakeholderType === STAKEHOLDER_EMERGENCY_RESPONDER;

  const successParagraphs = useMemo(() => {
    const outcome = registrationOutcomeFromResolvedStatus(resolvedStatus);
    const host = hostKind === "webview" ? "webview" : "browser";
    return registrationSuccessParagraphs(outcome, host);
  }, [resolvedStatus, hostKind]);

  useEffect(() => {
    if (urlParams.approvalCode) setApprovalCode(urlParams.approvalCode);
    if (urlParams.eventCode) setEventCodeSlug(urlParams.eventCode);
    // Campaign skip-QC must not override an invite referral that already carries a Quick Code.
    // DEV jump owns the initial step — don't pull back to accountBasics.
    if (devJump) return;
    if (urlParams.skipQuickCodePrompt && !urlParams.referralQuickCode) {
      setHasQuickCode(false);
      changeStep("accountBasics", "jump");
    }
  }, [
    urlParams.approvalCode,
    urlParams.eventCode,
    urlParams.skipQuickCodePrompt,
    urlParams.referralQuickCode,
    changeStep,
    devJump,
  ]);

  useEffect(() => {
    if (devJump) return;
    if (!urlParams.isExpressReturnMode || expressReturnBootstrapped.current) return;
    expressReturnBootstrapped.current = true;
    const qc = formatQuickCodeInput(urlParams.quickCode);
    setQuickCode(qc);
    setHasQuickCode(true);
    changeStep("expressReturnQuickCode", "jump");
  }, [urlParams.isExpressReturnMode, urlParams.quickCode, changeStep, devJump]);

  // Standard invite link: /user/register?referralCode=… (not mode=express). Prefill QC and skip Yes/No.
  useEffect(() => {
    if (devJump) return;
    if (urlParams.isExpressMode || urlParams.isExpressReturnMode) return;
    if (!urlParams.referralQuickCode || referralQuickCodeBootstrapped.current) return;
    referralQuickCodeBootstrapped.current = true;
    const qc = formatQuickCodeInput(urlParams.referralQuickCode);
    setQuickCode(qc);
    setHasQuickCode(true);
    setSubmitError(null);
    changeStep("quickCodeEntry", "jump");
  }, [
    urlParams.isExpressMode,
    urlParams.isExpressReturnMode,
    urlParams.referralQuickCode,
    changeStep,
    devJump,
  ]);

  const canProceedAccount = useMemo(
    () => effectiveAccountSchema.safeParse(form).success,
    [effectiveAccountSchema, form],
  );

  const canProceedJurisdictions = useMemo(
    () => registrationJurisdictionStepSchema.safeParse(extendedForm).success,
    [extendedForm],
  );

  const canProceedMailing = useMemo(
    () => registrationMailingStepSchema.safeParse(extendedForm).success,
    [extendedForm],
  );

  const canProceedOrganization = useMemo(
    () =>
      registrationOrganizationStepSchema(wizardStakeholderType, form.jobTitle).safeParse(extendedForm)
        .success,
    [extendedForm, wizardStakeholderType, form.jobTitle],
  );

  const canProceedAgencyInfo = useMemo(
    () => registrationAgencyInfoStepSchema(wizardStakeholderType).safeParse(extendedForm).success,
    [extendedForm, wizardStakeholderType],
  );

  const canProceedAgencyTraining = useMemo(
    () => registrationAgencyTrainingStepSchema().safeParse(extendedForm).success,
    [extendedForm],
  );

  const canProceedDispatchPart = useMemo(
    () => registrationDispatchCapabilitiesPartSchema(dispatchPart).safeParse(extendedForm).success,
    [extendedForm, dispatchPart],
  );

  const dispatchFieldErrors = useMemo(() => {
    const r = registrationDispatchCapabilitiesStepSchema().safeParse(extendedForm);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      dispatchFacilityClassification: fe.dispatchFacilityClassification?.[0],
      dispatchDisciplinesServedByAgency: fe.dispatchDisciplinesServedByAgency?.[0],
      dispatchMainAreaOfResponsibility: fe.dispatchMainAreaOfResponsibility?.[0],
      dispatchEstPopulationOfResponsibilityArea: fe.dispatchEstPopulationOfResponsibilityArea?.[0],
      dispatchComputerAidedDispatchSystemUsed: fe.dispatchComputerAidedDispatchSystemUsed?.[0],
      dispatchComputerAidedDispatchSystemUsedOther: fe.dispatchComputerAidedDispatchSystemUsedOther?.[0],
      dispatchCanDetermineCallerLatLongGis: fe.dispatchCanDetermineCallerLatLongGis?.[0],
      dispatchProgramsUsedByAgency: fe.dispatchProgramsUsedByAgency?.[0],
      dispatchRadioSystemsUsedByAgency: fe.dispatchRadioSystemsUsedByAgency?.[0],
      dispatchRadioSystemsUsedByAgencyOther: fe.dispatchRadioSystemsUsedByAgencyOther?.[0],
      dispatchPublicNotificationSystemUsed: fe.dispatchPublicNotificationSystemUsed?.[0],
      dispatchPublicNotificationSystemUsedOther: fe.dispatchPublicNotificationSystemUsedOther?.[0],
      dispatchIsContinuingEducationRequired: fe.dispatchIsContinuingEducationRequired?.[0],
      dispatchDoesStateRequireCertification: fe.dispatchDoesStateRequireCertification?.[0],
      dispatchDoesStateRequireCertificationOther:
        fe.dispatchDoesStateRequireCertificationOther?.[0],
      dispatchTrainingCoversEmergencyTopics: fe.dispatchTrainingCoversEmergencyTopics?.[0],
      dispatchHasAccessToEmergencyTopics: fe.dispatchHasAccessToEmergencyTopics?.[0],
      dispatchSopSogForEmergencyTopics: fe.dispatchSopSogForEmergencyTopics?.[0],
      dispatchCompletedCoursesOnEmergencyTopics: fe.dispatchCompletedCoursesOnEmergencyTopics?.[0],
    };
  }, [extendedForm]);

  const agencyFieldErrors = useMemo(() => {
    const r = registrationAgencyInfoStepSchema(wizardStakeholderType).safeParse(extendedForm);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      typeOfAgency: fe.typeOfAgency?.[0],
      nameOfAgencyCommandingOfficer: fe.nameOfAgencyCommandingOfficer?.[0],
      nonEmergencyNumberForAgencyOrDepartment: fe.nonEmergencyNumberForAgencyOrDepartment?.[0],
      website: fe.website?.[0],
      staffed: fe.staffed?.[0],
      numberOfPeopleInDepartment: fe.numberOfPeopleInDepartment?.[0],
      dispatchRadioSystemsUsedByAgency: fe.dispatchRadioSystemsUsedByAgency?.[0],
      dispatchRadioSystemsUsedByAgencyOther: fe.dispatchRadioSystemsUsedByAgencyOther?.[0],
      dispatchIsRadioSystemP25Compliant: fe.dispatchIsRadioSystemP25Compliant?.[0],
      dispatchIsRadioSystemEncrypted: fe.dispatchIsRadioSystemEncrypted?.[0],
    };
  }, [extendedForm, wizardStakeholderType]);

  const additionalQuestionsFieldErrors = useMemo(() => {
    const r = registrationAdditionalQuestionsStepSchema.safeParse(extendedForm);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      howDidYouFindOutAboutBuxus: fe.howDidYouFindOutAboutBuxus?.[0],
      howDidYouFindOutAboutBuxusAgency: fe.howDidYouFindOutAboutBuxusAgency?.[0],
      howDidYouFindOutAboutBuxusAssociation: fe.howDidYouFindOutAboutBuxusAssociation?.[0],
      howDidYouFindOutAboutBuxusConference: fe.howDidYouFindOutAboutBuxusConference?.[0],
      howDidYouFindOutAboutBuxusOperator: fe.howDidYouFindOutAboutBuxusOperator?.[0],
      howDidYouFindOutAboutBuxusTrainer: fe.howDidYouFindOutAboutBuxusTrainer?.[0],
      howDidYouFindOutAboutBuxusWebinar: fe.howDidYouFindOutAboutBuxusWebinar?.[0],
    };
  }, [extendedForm]);

  const hazwoperError = useMemo(() => {
    const r = registrationAgencyTrainingStepSchema().safeParse(extendedForm);
    if (r.success) return undefined;
    return r.error.flatten().fieldErrors.hazwoper?.[0];
  }, [extendedForm]);

  /** Live Zod messages under account fields (avoids “Next disabled” with no hint when defaults were wrong). */
  const accountFieldErrors = useMemo((): Partial<Record<keyof RegistrationAccountValues, string>> => {
    const r = effectiveAccountSchema.safeParse(form);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors as Partial<Record<keyof RegistrationAccountValues, string[]>>;
    return {
      stakeholderType: fe.stakeholderType?.[0],
      jobTitle: fe.jobTitle?.[0],
      isUnaffiliatedProfessionalErTrainer: fe.isUnaffiliatedProfessionalErTrainer?.[0],
      communicationsRole: fe.communicationsRole?.[0],
      firstName: fe.firstName?.[0],
      lastName: fe.lastName?.[0],
      phone: fe.phone?.[0],
      email: fe.email?.[0],
      password: fe.password?.[0],
      passwordConfirm: fe.passwordConfirm?.[0],
    };
  }, [effectiveAccountSchema, form]);

  const organizationFieldErrors = useMemo(() => {
    const r = registrationOrganizationStepSchema(wizardStakeholderType, form.jobTitle).safeParse(
      extendedForm,
    );
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      institution: fe.institution?.[0],
      organizationType: fe.organizationType?.[0],
      department: fe.department?.[0],
      institutionPhone: fe.institutionPhone?.[0],
    };
  }, [extendedForm, wizardStakeholderType, form.jobTitle]);

  const jurisdictionRowLiveErrors = useMemo((): JurisdictionFieldErrors[] => {
    const r = registrationJurisdictionStepSchema.safeParse(extendedForm);
    if (r.success) return extendedForm.jurisdictions.map(() => ({}));
    const rows: JurisdictionFieldErrors[] = extendedForm.jurisdictions.map(() => ({}));
    for (const issue of r.error.issues) {
      const path = issue.path;
      if (path[0] === "jurisdictions" && typeof path[1] === "number") {
        const idx = path[1] as number;
        const field = path[2];
        if (field === "country" || field === "state" || field === "county") {
          rows[idx] = { ...rows[idx], [field]: issue.message };
        }
      }
    }
    return rows;
  }, [extendedForm]);

  const mailingFieldErrors = useMemo(() => {
    const r = registrationMailingStepSchema.safeParse(extendedForm);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      mailingAddress1: fe.mailingAddress1?.[0],
      mailingCity: fe.mailingCity?.[0],
      mailingCountry: fe.mailingCountry?.[0],
      mailingState: fe.mailingState?.[0],
      mailingCounty: fe.mailingCounty?.[0],
      mailingZip: fe.mailingZip?.[0],
    };
  }, [extendedForm]);

  const firstJurisdictionComplete = useMemo(() => {
    const r0 = extendedForm.jurisdictions[0];
    return !!(r0?.country?.trim() && r0?.state?.trim() && r0?.county?.trim());
  }, [extendedForm.jurisdictions]);

  function showAccountFieldError(field: keyof RegistrationAccountValues): string | undefined {
    const msg = accountFieldErrors[field];
    if (!msg) return undefined;
    if (accountSubmitAttempted) return msg;
    if (accountBlurred[field]) return msg;
    return undefined;
  }

  function showJurisdictionCellError(
    index: number,
    field: keyof RegistrationJurisdictionRow,
  ): string | undefined {
    const msg = jurisdictionRowLiveErrors[index]?.[field];
    if (!msg) return undefined;
    if (jurisdictionSubmitAttempted) return msg;
    if (jurisdictionCellBlurred[`${index}-${field}`]) return msg;
    if (field === "county" && msg.includes("already listed")) return msg;
    return undefined;
  }

  function showMailingFieldError(
    field: "mailingAddress1" | "mailingCity" | "mailingCountry" | "mailingState" | "mailingCounty" | "mailingZip",
  ): string | undefined {
    const msg = mailingFieldErrors[field];
    if (!msg) return undefined;
    if (mailingSubmitAttempted) return msg;
    if (mailingBlurred[field]) return msg;
    return undefined;
  }

  function showOrgFieldError(
    field: "institution" | "organizationType" | "department" | "institutionPhone",
  ): string | undefined {
    const msg = organizationFieldErrors[field];
    if (!msg) return undefined;
    if (orgSubmitAttempted) return msg;
    if (orgBlurred[field]) return msg;
    return undefined;
  }

  function showDispatchFieldError(
    field:
      | "dispatchFacilityClassification"
      | "dispatchDisciplinesServedByAgency"
      | "dispatchMainAreaOfResponsibility"
      | "dispatchEstPopulationOfResponsibilityArea"
      | "dispatchComputerAidedDispatchSystemUsed"
      | "dispatchComputerAidedDispatchSystemUsedOther"
      | "dispatchCanDetermineCallerLatLongGis"
      | "dispatchProgramsUsedByAgency"
      | "dispatchRadioSystemsUsedByAgency"
      | "dispatchRadioSystemsUsedByAgencyOther"
      | "dispatchPublicNotificationSystemUsed"
      | "dispatchPublicNotificationSystemUsedOther"
      | "dispatchIsContinuingEducationRequired"
      | "dispatchDoesStateRequireCertification"
      | "dispatchDoesStateRequireCertificationOther"
      | "dispatchTrainingCoversEmergencyTopics"
      | "dispatchHasAccessToEmergencyTopics"
      | "dispatchSopSogForEmergencyTopics"
      | "dispatchCompletedCoursesOnEmergencyTopics",
  ): string | undefined {
    const msg = dispatchFieldErrors[field];
    if (!msg) return undefined;
    if (dispatchSubmitAttempted) return msg;
    if (dispatchBlurred[field]) return msg;
    return undefined;
  }

  function showAgencyFieldError(
    field:
      | "typeOfAgency"
      | "nameOfAgencyCommandingOfficer"
      | "nonEmergencyNumberForAgencyOrDepartment"
      | "website"
      | "staffed"
      | "numberOfPeopleInDepartment"
      | "dispatchRadioSystemsUsedByAgency"
      | "dispatchRadioSystemsUsedByAgencyOther"
      | "dispatchIsRadioSystemP25Compliant"
      | "dispatchIsRadioSystemEncrypted",
  ): string | undefined {
    const msg = agencyFieldErrors[field];
    if (!msg) return undefined;
    if (agencySubmitAttempted) return msg;
    if (agencyBlurred[field]) return msg;
    return undefined;
  }

  function showAdditionalQuestionsFieldError(
    field:
      | "howDidYouFindOutAboutBuxus"
      | "howDidYouFindOutAboutBuxusAgency"
      | "howDidYouFindOutAboutBuxusAssociation"
      | "howDidYouFindOutAboutBuxusConference"
      | "howDidYouFindOutAboutBuxusOperator"
      | "howDidYouFindOutAboutBuxusTrainer"
      | "howDidYouFindOutAboutBuxusWebinar",
  ): string | undefined {
    const msg = additionalQuestionsFieldErrors[field];
    if (!msg) return undefined;
    if (additionalQuestionsSubmitAttempted) return msg;
    if (additionalQuestionsBlurred[field]) return msg;
    return undefined;
  }

  function profileStepZodResult(uiStep: Step) {
    switch (uiStep) {
      case "mailing":
        return registrationMailingStepSchema.safeParse(extendedForm);
      case "jurisdictions":
        return registrationJurisdictionStepSchema.safeParse(extendedForm);
      case "organization":
        return registrationOrganizationStepSchema(wizardStakeholderType, form.jobTitle).safeParse(
          extendedForm,
        );
      case "agencyInfo":
        return registrationAgencyInfoStepSchema(wizardStakeholderType).safeParse(extendedForm);
      case "agencyCapability":
        return registrationAgencyCapabilitiesStepSchema.safeParse(extendedForm);
      case "agencyProcedure":
        return registrationAgencyProceduresStepSchema.safeParse(extendedForm);
      case "agencyTraining":
        return registrationAgencyTrainingStepSchema().safeParse(extendedForm);
      case "dispatchCapabilities":
        return registrationDispatchCapabilitiesStepSchema().safeParse(extendedForm);
      case "agencyAgreement":
        return registrationMutualAidStepSchema.safeParse(extendedForm);
      case "additionalQuestions":
        return registrationAdditionalQuestionsStepSchema.safeParse(extendedForm);
      default:
        return { success: true as const, data: extendedForm };
    }
  }

  const PROFILE_STEP_FORM_NAMES: Partial<Record<Step, string>> = {
    mailing: "MailingAddressForm",
    jurisdictions: "JurisdictionsForm",
    organization: "OrganizationInfoForm",
    agencyInfo: "AgencyInfoForm",
    agencyCapability: "AgencyCapabilitiesForm",
    agencyProcedure: "AgencyProceduresForm",
    agencyTraining: "AgencyTrainingForm",
    dispatchCapabilities: "DispatchCapabilitiesForm",
    agencyAgreement: "MutualAidForm",
    additionalQuestions: "AdditionalQuestionsForm",
  };

  function trackProfileStepValidationFailed(uiStep: Step): void {
    const formName = PROFILE_STEP_FORM_NAMES[uiStep];
    if (!formName) return;
    const result = profileStepZodResult(uiStep);
    if (result.success) return;
    const { errorFields, errorMessages } = flattenZodFieldErrors(result.error);
    trackRegistrationFormValidationFailed({ formName, errorFields, errorMessages, ...regStepCtx });
  }

  function profileStepIsValid(uiStep: Step): boolean {
    return profileStepZodResult(uiStep).success;
  }

  function markProfileStepSubmitAttempted(uiStep: Step): void {
    switch (uiStep) {
      case "mailing":
        setMailingSubmitAttempted(true);
        break;
      case "jurisdictions":
        setJurisdictionSubmitAttempted(true);
        break;
      case "organization":
        setOrgSubmitAttempted(true);
        break;
      case "agencyInfo":
        setAgencySubmitAttempted(true);
        break;
      case "agencyAgreement":
        break;
      case "additionalQuestions":
        setAdditionalQuestionsSubmitAttempted(true);
        break;
      case "dispatchCapabilities":
        setDispatchSubmitAttempted(true);
        break;
      case "agencyTraining":
        setTrainingSubmitAttempted(true);
        break;
      default:
        break;
    }
  }

  const isLastProfileStep = useMemo(() => {
    const engineId = uiStepToEngineStep(step);
    if (!engineId) return false;
    return isLastRegistrationStep(stepContext, engineId);
  }, [step, stepContext]);

  const expressReturnFirstProfileStep = useMemo(() => {
    if (!urlParams.isExpressReturnMode) return null;
    return firstProfileStepId(stepContext);
  }, [urlParams.isExpressReturnMode, stepContext]);

  const hideExpressReturnBack = useMemo(() => {
    if (!urlParams.isExpressReturnMode || !expressReturnFirstProfileStep) return false;
    return step === engineStepToUiStep(expressReturnFirstProfileStep);
  }, [urlParams.isExpressReturnMode, expressReturnFirstProfileStep, step]);

  const advanceProfileStep = () => {
    setSubmitError(null);
    markProfileStepSubmitAttempted(step);
    if (!profileStepIsValid(step)) {
      trackProfileStepValidationFailed(step);
      return;
    }

    const engineId = uiStepToEngineStep(step);
    if (!engineId) return;

    if (isLastRegistrationStep(stepContext, engineId)) {
      void finalizeFullRegistration();
      return;
    }

    const next = nextRegistrationStep(stepContext, engineId);
    if (!next) {
      void finalizeFullRegistration();
      return;
    }

    if (next === "jurisdictions") {
      setExtendedForm((prev) => prepopulateFirstJurisdictionFromMailing(prev));
    }

    if (next === "dispatchCapabilities") {
      setDispatchPart(1);
    }

    changeStep(engineStepToUiStep(next), "forward");
  };

  const advanceDispatchPart = () => {
    setSubmitError(null);
    setDispatchSubmitAttempted(true);
    const dispatchParse = registrationDispatchCapabilitiesPartSchema(dispatchPart).safeParse(extendedForm);
    if (!dispatchParse.success) {
      const { errorFields, errorMessages } = flattenZodFieldErrors(dispatchParse.error);
      trackRegistrationFormValidationFailed({
        formName: "DispatchCapabilitiesForm",
        errorFields,
        errorMessages,
        ...regStepCtx,
      });
      return;
    }

    if (dispatchPart < DISPATCH_CAPABILITIES_PART_COUNT) {
      setDispatchPart((part) => (part + 1) as DispatchCapabilitiesPart);
      setDispatchSubmitAttempted(false);
      return;
    }

    advanceProfileStep();
  };

  const goBackDispatchPart = () => {
    setSubmitError(null);
    if (dispatchPart > 1) {
      setDispatchPart((part) => (part - 1) as DispatchCapabilitiesPart);
      return;
    }
    goBackProfileStep();
  };

  const goBackProfileStep = () => {
    if (hideExpressReturnBack) return;
    setSubmitError(null);
    const engineId = uiStepToEngineStep(step);
    if (!engineId) return;
    const prev = previousRegistrationStep(stepContext, engineId);
    if (prev) changeStep(engineStepToUiStep(prev), "backward");
  };

  const mailingStateOptions = useMemo(
    () => listRegistrationStatesForCountry(extendedForm.mailingCountry),
    [extendedForm.mailingCountry],
  );

  const mailingCountyOptions = useMemo(
    () => listRegistrationCountiesForState(extendedForm.mailingCountry, extendedForm.mailingState),
    [extendedForm.mailingCountry, extendedForm.mailingState],
  );

  useEffect(() => {
    if (committedAccountEmail && form.email.trim() !== committedAccountEmail) {
      setAbpUserId(null);
      setCommittedAccountEmail(null);
    }
  }, [form.email, committedAccountEmail]);

  useEffect(() => {
    if (!qcResumeEmailSnapshot) return;
    if (form.email.trim().toLowerCase() !== qcResumeEmailSnapshot.toLowerCase()) {
      setOmitPasswordAfterQc(false);
      setQcResumeEmailSnapshot(null);
    }
  }, [form.email, qcResumeEmailSnapshot]);

  const resetRegistrationWizard = () => {
    changeStep("quickCode", "jump");
    setHasQuickCode(null);
    setQuickCode("");
    setSubmitError(null);
    setOmitPasswordAfterQc(false);
    setQcResumeEmailSnapshot(null);
    setQuickCodeBlockedEmail(null);
    setForm({ ...DEFAULT_VALUES });
    setExtendedForm(registrationExtendedDefaults());
    setAbpUserId(null);
    setCommittedAccountEmail(null);
    setResolvedStatus(null);
    setApprovalCode(urlParams.approvalCode || "");
    setEventCodeSlug(urlParams.eventCode || null);
  };

  const setField = <K extends keyof RegistrationAccountValues>(key: K, value: RegistrationAccountValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setExt = <K extends Exclude<keyof RegistrationExtendedFormValues, "jurisdictions">>(
    key: K,
    value: RegistrationExtendedFormValues[K],
  ) => {
    setExtendedForm((prev) => ({ ...prev, [key]: value }));
  };

  const patchExtended = (partial: Partial<RegistrationExtendedFormValues>) => {
    setExtendedForm((prev) => ({ ...prev, ...partial }));
  };

  const patchJurisdiction = (index: number, partial: Partial<RegistrationJurisdictionRow>) => {
    setExtendedForm((prev) => ({
      ...prev,
      jurisdictions: prev.jurisdictions.map((row, i) => (i === index ? { ...row, ...partial } : row)),
    }));
  };

  const addJurisdictionRow = () => {
    setExtendedForm((prev) => {
      if (prev.jurisdictions.length >= 5) return prev;
      return { ...prev, jurisdictions: [...prev.jurisdictions, defaultJurisdictionRow()] };
    });
  };

  const removeJurisdictionRow = (index: number) => {
    setExtendedForm((prev) => ({
      ...prev,
      jurisdictions: prev.jurisdictions.filter((_, i) => i !== index),
    }));
  };

  const setMailingCountry = (country: string) => {
    setExtendedForm((prev) => ({
      ...prev,
      mailingCountry: country,
      mailingState: "",
      mailingCounty: "",
    }));
  };

  const setMailingState = (state: string) => {
    setExtendedForm((prev) => {
      const counties = listRegistrationCountiesForState(prev.mailingCountry, state);
      const nextCounty = counties.includes(prev.mailingCounty) ? prev.mailingCounty : "";
      return { ...prev, mailingState: state, mailingCounty: nextCounty };
    });
  };

  const onCancel = () => leaveRegistrationHost(navigate);

  /** `POST /api/buxus/get-by-quickcode-computed` → map CRM contact into wizard state. */
  const submitQuickCodeLookup = async () => {
    if (!hasQuickCodeInput(quickCode)) return;
    setSubmitError(null);
    setQuickCodeBlockedEmail(null);
    setQuickCodeLookupLoading(true);
    try {
      const raw = await getContactByQuickCodeComputed(quickCodeForLookup(quickCode));
      const parsed = parseQuickCodeLookupResponse(raw);
      if (!parsed.ok) {
        trackRegistrationErrorOccurred("quick_code_not_found", regFlowCtx, {
          quickCode: quickCodeForLookup(quickCode),
        });
        showQuickCodeLookupErrorModal();
        return;
      }

      const contact = parsed.contact;
      trackRegistrationQuickCodeFetched({
        quickCode: quickCodeForLookup(quickCode),
        hasData: true,
        stakeholderType:
          typeof contact.stakeholder_Type === "string"
            ? contact.stakeholder_Type
            : typeof contact.stakeholderType === "string"
              ? contact.stakeholderType
              : undefined,
        email: typeof contact.email === "string" ? contact.email : undefined,
        ...regFlowCtx,
      });

      const mapped = mapQuickCodeContactToForms(contact);
      const email = (mapped.form.email ?? "").trim();

      let omitPwd = false;
      if (email) {
        try {
          const existing = await findUserByEmailInBuxusTracked(email, regFlowCtx);
          const zohoCrmId = existing?.zohoCrmId?.trim();
          if (zohoCrmId) {
            setQuickCodeBlockedEmail(email);
            changeStep("quickCodeAlreadyRegistered", "forward");
            return;
          }
          const uid = existing?.userId?.trim();
          omitPwd = !!uid;
        } catch (error) {
          reportRegistrationFailureIfServer(error, "quickCodeEmailCheck");
          setSubmitError(friendlyRegistrationError(error));
          return;
        }
      }

      setOmitPasswordAfterQc(omitPwd);
      setQcResumeEmailSnapshot(omitPwd ? email : null);

      const mergedAccount = mergeAccountFromQuickCode(DEFAULT_VALUES, mapped.form);
      setForm(
        omitPwd
          ? { ...mergedAccount, password: "", passwordConfirm: "" }
          : mergedAccount,
      );
      setExtendedForm(mergeExtendedFromQuickCode(registrationExtendedDefaults(), mapped.extended));
      setQuickCode(mapped.quickCodeForCrm || quickCodeForLookup(quickCode));
      changeStep("accountBasics", "forward");
    } catch (error) {
      reportRegistrationFailureIfServer(error, "quickCodeLookup");
      trackRegistrationErrorOccurred("quick_code_fetch_failed", regFlowCtx, {
        quickCode: quickCodeForLookup(quickCode),
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      showQuickCodeLookupErrorModal();
    } finally {
      setQuickCodeLookupLoading(false);
    }
  };

  /** Legacy express-return `QuickCodeForm` → lookup QC, resolve existing ABP user, skip to profile steps. */
  const submitExpressReturnQuickCode = async () => {
    if (!hasQuickCodeInput(quickCode)) return;
    setSubmitError(null);
    setQuickCodeLookupLoading(true);
    try {
      const raw = await getContactByQuickCodeComputed(quickCodeForLookup(quickCode));
      const parsed = parseQuickCodeLookupResponse(raw);
      if (!parsed.ok) {
        trackRegistrationErrorOccurred("quick_code_not_found", regFlowCtx, {
          quickCode: quickCodeForLookup(quickCode),
        });
        showQuickCodeLookupErrorModal();
        return;
      }

      const contact = parsed.contact;
      trackRegistrationQuickCodeFetched({
        quickCode: quickCodeForLookup(quickCode),
        hasData: true,
        stakeholderType:
          typeof contact.stakeholder_Type === "string"
            ? contact.stakeholder_Type
            : typeof contact.stakeholderType === "string"
              ? contact.stakeholderType
              : undefined,
        email: typeof contact.email === "string" ? contact.email : undefined,
        ...regFlowCtx,
      });

      try {
      const mapped = mapQuickCodeContactToForms(contact);
      const email = (mapped.form.email ?? "").trim();
      if (!email) {
        setSubmitError(
          "Unable to continue: account email was not loaded from Quick Code data.",
        );
        return;
      }

      const existing = await findUserByEmailInBuxusTracked(email, regFlowCtx);
      const userId = existing?.userId?.trim();
      if (!userId) {
        setSubmitError(
          "Unable to continue: account id could not be resolved for this registration.",
        );
        return;
      }

      const mergedAccount = mergeAccountFromQuickCode(DEFAULT_VALUES, mapped.form);
      const mergedExtended = mergeExtendedFromQuickCode(registrationExtendedDefaults(), mapped.extended);
      const resolvedSt = resolveRegistrationWizardStakeholderType(
        mergedAccount.stakeholderType,
        mergedAccount.jobTitle,
      );
      const returnCtx: RegistrationStepContext = {
        stakeholderType: resolvedSt,
        jobTitle: mergedAccount.jobTitle,
        typeOfAgency: mergedExtended.typeOfAgency,
      };
      const firstProfile = firstProfileStepId(returnCtx);
      if (!firstProfile) {
        setSubmitError("Unable to continue: no profile steps are available for this registration.");
        return;
      }

      setAbpUserId(userId);
      setCommittedAccountEmail(email);
      setOmitPasswordAfterQc(true);
      setQcResumeEmailSnapshot(email);
      setForm({ ...mergedAccount, password: "", passwordConfirm: "" });
      setExtendedForm(
        firstProfile === "jurisdictions"
          ? prepopulateFirstJurisdictionFromMailing(mergedExtended)
          : mergedExtended,
      );
      setQuickCode(mapped.quickCodeForCrm || quickCodeForLookup(quickCode));
      changeStep(engineStepToUiStep(firstProfile), "jump");
      } catch (error) {
        reportRegistrationFailureIfServer(error, "expressReturnQuickCodeResolve");
        trackRegistrationErrorOccurred("express_return_resolve_failed", regFlowCtx, {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        setSubmitError(friendlyRegistrationError(error));
      }
    } catch (error) {
      reportRegistrationFailureIfServer(error, "expressReturnQuickCodeLookup");
      trackRegistrationErrorOccurred("quick_code_fetch_failed", regFlowCtx, {
        quickCode: quickCodeForLookup(quickCode),
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      showQuickCodeLookupErrorModal();
    } finally {
      setQuickCodeLookupLoading(false);
    }
  };

  /** Legacy `RegisterShortForm`: ensure ABP user exists, then continue to CRM collection steps. */
  const submitAccountBasics = async () => {
    setSubmitError(null);
    const parsed = effectiveAccountSchema.safeParse(form);
    if (!parsed.success) {
      setAccountSubmitAttempted(true);
      const { errorFields, errorMessages } = flattenZodFieldErrors(parsed.error);
      trackRegistrationFormValidationFailed({
        formName: "RegisterShortForm",
        errorFields,
        errorMessages,
        ...regStepCtx,
      });
      return;
    }

    const emailKey = parsed.data.email.trim();
    if (abpUserId && committedAccountEmail === emailKey) {
      changeStep("mailing", "forward");
      return;
    }

    setBasicsSubmitting(true);
    try {
      const existing = await findUserByEmailInBuxusTracked(parsed.data.email, regFlowCtx);
      const existingUserId = existing?.userId?.trim() || null;
      const canContinueExpressProfileUpdate =
        urlParams.isExpressReturnMode && hasQuickCode === true;

      // Quick Code / CRM invite: email may already map to an ABP user (and even ZohoCrmId) from a prior
      // registration attempt or admin setup. Legacy blocked here; we reuse userId and let account-setup-crm upsert.
      if (hasQuickCode === true && existingUserId) {
        setAbpUserId(existingUserId);
        setCommittedAccountEmail(emailKey);
        setOmitPasswordAfterQc(canContinueExpressProfileUpdate || omitPasswordAfterQc);
        changeStep("mailing", "forward");
        return;
      }

      if (existing?.zohoCrmId?.trim() && !canContinueExpressProfileUpdate) {
        const duplicateMsg =
          "An account already exists with this email. Please use Login or reset your password.";
        if (hostKind === "webview") {
          postDisplayErrorModal(duplicateMsg);
          return;
        }
        setSubmitError(duplicateMsg);
        return;
      }

      if (existingUserId) {
        setAbpUserId(existingUserId);
        setCommittedAccountEmail(emailKey);
        changeStep("mailing", "forward");
        return;
      }

      const fullAccount = registrationAccountSchema.safeParse(form);
      if (!fullAccount.success) {
        setAccountSubmitAttempted(true);
        const { errorFields, errorMessages } = flattenZodFieldErrors(fullAccount.error);
        trackRegistrationFormValidationFailed({
          formName: "RegisterShortForm",
          errorFields,
          errorMessages,
          ...regStepCtx,
        });
        setSubmitError("Enter and confirm a password to create your Buxus account.");
        return;
      }

      const out = await registerBuxusAccountTracked(toRegisterBuxusRequest(fullAccount.data), regStepCtx);
      if (!out?.userId) {
        setSubmitError("Registration did not return a user id. Please try again or contact support.");
        return;
      }
      setAbpUserId(out.userId);
      setCommittedAccountEmail(emailKey);
      changeStep("mailing", "forward");
    } catch (error) {
      reportRegistrationFailureIfServer(error, "accountBasics");
      setSubmitError(friendlyRegistrationError(error));
    } finally {
      setBasicsSubmitting(false);
    }
  };

  const finalizeFullRegistration = async () => {
    setSubmitError(null);
    // Express return and QC-resume paths skip the account step and never re-validate first/last name at CRM submit.
    let accountForCrm: RegistrationAccountValues | RegistrationAccountResumeValues;
    if (skipsPasswordOnAccount) {
      accountForCrm = form;
    } else {
      const parsedAccount = effectiveAccountSchema.safeParse(form);
      if (!parsedAccount.success) {
        const { errorFields, errorMessages } = flattenZodFieldErrors(parsedAccount.error);
        trackRegistrationFormValidationFailed({
          formName: "RegisterShortForm",
          errorFields,
          errorMessages,
          ...regStepCtx,
        });
        const fe = parsedAccount.error.flatten().fieldErrors;
        const firstFieldMsg = Object.values(fe).flat().find(Boolean);
        setSubmitError(firstFieldMsg ?? "Please go back and fix your account details.");
        return;
      }
      accountForCrm = parsedAccount.data;
    }
    const visibleSteps = getVisibleRegistrationSteps(stepContext);
    for (const engineStep of visibleSteps) {
      if (engineStep === "quickCode" || engineStep === "account") continue;
      const ui = engineStepToUiStep(engineStep);
      if (!profileStepIsValid(ui)) {
        setSubmitError(`Please complete the ${profileStepLabel(engineStep)} step.`);
        if (engineStep === "dispatchCapabilities") {
          setDispatchPart(firstInvalidDispatchCapabilitiesPart(extendedForm));
        }
        changeStep(ui, "jump");
        markProfileStepSubmitAttempted(ui);
        trackProfileStepValidationFailed(ui);
        return;
      }
    }
    if (!abpUserId) {
      setSubmitError("Session expired; please go back and re-enter your account details.");
      return;
    }

    setCrmSubmitting(true);
    try {
      const body = buildAccountSetupCrmPayload({
        abpUserId,
        account: accountForCrm,
        extended: extendedForm,
        quickCode: hasQuickCode ? quickCode : "",
        approvalCode: approvalCode || null,
        eventCodeSlug: eventCodeSlug,
        omitEmptyAccountFields: skipsPasswordOnAccount,
      });
      const res = await accountSetupCrmTracked(body, {
        userId: abpUserId,
        emailAddress: accountForCrm.email.trim(),
        stakeholderType: crmStakeholderType,
        hasQuickCode: hasQuickCode === true && !!quickCode.trim(),
        hasReferrerContactId: !!urlParams.referrerContactId,
        ...regFlowCtx,
      });
      const { ok, message, resolvedStatus: crmStatus } = parseAccountSetupCrmResponse(res);
      if (!ok) {
        setSubmitError(message ?? REGISTRATION_FAILURE_MESSAGE);
        return;
      }
      setResolvedStatus(crmStatus ?? null);
      changeStep("done", "forward");
    } catch (error) {
      reportRegistrationFailureIfServer(error, "accountSetupCrm");
      setSubmitError(friendlyRegistrationError(error));
    } finally {
      setCrmSubmitting(false);
    }
  };

  const busy = basicsSubmitting || crmSubmitting;

  function profileStepLabel(engineStep: RegistrationStepId): string {
    switch (engineStep) {
      case "mailing":
        return "mailing address";
      case "jurisdictions":
        return "jurisdictions";
      case "organization":
        return "organization";
      case "agencyInfo":
        return "agency";
      case "agencyCapability":
        return "agency capabilities";
      case "agencyProcedure":
        return "agency procedures";
      case "agencyTraining":
        return "agency training";
      case "dispatchCapabilities":
        return "dispatch capabilities";
      case "agencyAgreement":
        return "mutual aid";
      case "additionalQuestions":
        return "additional questions";
      default:
        return "profile";
    }
  }

  function profileStepNextLabel(): string {
    if (crmSubmitting && isLastProfileStep) return "Finishing registration…";
    if (isLastProfileStep) return "Submit";
    return "Next";
  }

  // Signed-in is local (token + userId in AbpProvider). No wait on remote probeSession.
  if (isAuthenticated) {
    return (
      <RegistrationShell hostKind={hostKind}>
        <section className="reg-card">
          <h2 className="reg-step-title">Already Signed In</h2>
          <p className="reg-step-lead">
            You are signed in to Buxus. To register a new account, please sign out first.
          </p>
          <div className="reg-actions">
            <button type="button" className="reg-btn primary" onClick={() => void logout()}>
              Log Out
            </button>
            <button type="button" className="reg-btn subtle" onClick={() => leaveRegistrationHost(navigate)}>
              Home
            </button>
          </div>
        </section>
      </RegistrationShell>
    );
  }

  return (
    <RegistrationShell hostKind={hostKind}>
        {step === "quickCode" ? (
          <section className="reg-card">
            <p className="reg-question">Did you receive a Quick Code for registration?</p>
            <p className="reg-step-lead reg-question-hint">
              If you were invited to register, your Quick Code should be in the invitation email you
              received from Buxus.
            </p>
            <div className="reg-choice-row">
              <button
                type="button"
                className={`reg-choice ${hasQuickCode === true ? "selected" : ""}`}
                onClick={() => {
                  setHasQuickCode(true);
                  setSubmitError(null);
                  setOmitPasswordAfterQc(false);
                  setQcResumeEmailSnapshot(null);
                  setForm({ ...DEFAULT_VALUES });
                  setExtendedForm(registrationExtendedDefaults());
                  setQuickCode("");
                  changeStep("quickCodeEntry", "forward");
                }}
              >
                Yes
              </button>
              <button
                type="button"
                className={`reg-choice ${hasQuickCode === false ? "selected" : ""}`}
                onClick={() => {
                  setHasQuickCode(false);
                  setQuickCode("");
                  setSubmitError(null);
                  setOmitPasswordAfterQc(false);
                  setQcResumeEmailSnapshot(null);
                  setForm({ ...DEFAULT_VALUES });
                  setExtendedForm(registrationExtendedDefaults());
                  changeStep("accountBasics", "forward");
                }}
              >
                No
              </button>
            </div>
            <div className="reg-actions reg-actions-intro">
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </section>
        ) : null}

        {step === "expressReturnQuickCode" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">{urlParams.eventTitle || "Register"}</h2>
            <p className="reg-step-lead reg-step-lead--tight">
              Let&apos;s finish registration by completing your profile.
            </p>
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button
                type="button"
                className="reg-btn primary"
                disabled={!hasQuickCodeInput(quickCode) || quickCodeLookupLoading}
                onClick={() => void submitExpressReturnQuickCode()}
              >
                {quickCodeLookupLoading ? "Loading…" : "Continue"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "quickCodeEntry" ? (
          <section className="reg-card">
            <p className="reg-step-lead reg-step-lead--tight">
              {urlParams.referralQuickCode
                ? "Click Start to register with your Quick Code."
                : "Enter the code from your invitation."}
            </p>
            <div className="reg-grid">
              <div className="reg-field">
                <input
                  className="reg-input-mono"
                  value={quickCode}
                  onChange={(e) => setQuickCode(formatQuickCodeInput(e.target.value))}
                  placeholder={`e.g. ${QUICK_CODE_INPUT_PLACEHOLDER}`}
                  aria-label="Quick Code"
                  aria-describedby="reg-quick-code-hint"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                  maxLength={15}
                />
                <span id="reg-quick-code-hint" className="reg-field-hint">
                  Example format: {QUICK_CODE_INPUT_PLACEHOLDER}
                </span>
              </div>
            </div>
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              {urlParams.referralQuickCode ? null : (
                <button
                  type="button"
                  className="reg-btn subtle"
                  onClick={() => {
                    setSubmitError(null);
                    setQuickCode("");
                    changeStep("quickCode", "backward");
                  }}
                >
                  Back
                </button>
              )}
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!hasQuickCodeInput(quickCode) || quickCodeLookupLoading}
                onClick={() => void submitQuickCodeLookup()}
              >
                {quickCodeLookupLoading ? "Looking up…" : "Start"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "quickCodeAlreadyRegistered" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">Registration Already Complete</h2>
            <p className="reg-step-lead">
              This Quick Code is linked to{" "}
              {quickCodeBlockedEmail ? (
                <strong>{quickCodeBlockedEmail}</strong>
              ) : (
                "the email on your invitation"
              )}
              , and that address already has a completed Buxus registration. Sign in with your existing account.
            </p>
            <p className="reg-step-lead">
              If you forgot your password, use the reset option on the login screen.
            </p>
            <div className="reg-actions">
              <button type="button" className="reg-btn primary" onClick={resetRegistrationWizard}>
                Start Over
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Sign In
              </button>
            </div>
          </section>
        ) : null}

        {step === "accountBasics" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">Account</h2>
            {skipsPasswordOnAccount ? (
              <p className="reg-step-lead reg-info-banner">
                You already started registration with this email; your Buxus password is set. Confirm or update the
                details below, then continue to finish your profile.
              </p>
            ) : (
              <p className="reg-step-lead">
                {hasQuickCode
                  ? "Your invitation details are pre-filled below. Choose a password and confirm everything looks correct before continuing."
                  : "Create your Buxus login. A few more questions follow on the next screens."}
              </p>
            )}
            <div className="reg-grid">
              <label className="reg-field">
                <RegReqLabel required>Are You a:</RegReqLabel>
                <select
                  value={form.stakeholderType}
                  onChange={(e) => setField("stakeholderType", e.target.value)}
                  onBlur={() => setAccountBlurred((p) => ({ ...p, stakeholderType: true }))}
                >
                  <option value="">Select</option>
                  {STAKEHOLDER_OPTIONS.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
                {showAccountFieldError("stakeholderType") ? (
                  <small className="reg-field-error">{showAccountFieldError("stakeholderType")}</small>
                ) : null}
              </label>

              {showJobTitleField ? (
                <label className="reg-field">
                  <RegReqLabel required>Job Title</RegReqLabel>
                  <select
                    value={form.jobTitle}
                    onChange={(e) => {
                      const next = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        jobTitle: next,
                        communicationsRole: requiresCommunicationsRoleJobTitle(next)
                          ? prev.communicationsRole
                          : "",
                      }));
                    }}
                    onBlur={() => setAccountBlurred((p) => ({ ...p, jobTitle: true }))}
                  >
                    <option value="">Select</option>
                    {JOB_TITLE_OPTIONS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  {showAccountFieldError("jobTitle") ? (
                    <small className="reg-field-error">{showAccountFieldError("jobTitle")}</small>
                  ) : null}
                </label>
              ) : null}

              {requiresCommunicationsRoleJobTitle(form.jobTitle) ? (
                <label className="reg-field">
                  <RegReqLabel required>Communications Role</RegReqLabel>
                  <select
                    value={form.communicationsRole}
                    onChange={(e) => setField("communicationsRole", e.target.value)}
                    onBlur={() => setAccountBlurred((p) => ({ ...p, communicationsRole: true }))}
                  >
                    <option value="">Select</option>
                    {COMMUNICATIONS_ROLE_OPTIONS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  {showAccountFieldError("communicationsRole") ? (
                    <small className="reg-field-error">{showAccountFieldError("communicationsRole")}</small>
                  ) : null}
                </label>
              ) : null}

              {showJobTitleField ? (
                <label className="reg-field reg-field--full">
                  <RegReqLabel required>
                    Are you a professional ER trainer and not affiliated with any specific response
                    organization (other than a trade association)?
                  </RegReqLabel>
                  <select
                    value={form.isUnaffiliatedProfessionalErTrainer}
                    onChange={(e) => setField("isUnaffiliatedProfessionalErTrainer", e.target.value)}
                    onBlur={() =>
                      setAccountBlurred((p) => ({ ...p, isUnaffiliatedProfessionalErTrainer: true }))
                    }
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {showAccountFieldError("isUnaffiliatedProfessionalErTrainer") ? (
                    <small className="reg-field-error">
                      {showAccountFieldError("isUnaffiliatedProfessionalErTrainer")}
                    </small>
                  ) : null}
                </label>
              ) : null}

              <label className="reg-field">
                <RegReqLabel required>First Name</RegReqLabel>
                <input
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  onBlur={() => setAccountBlurred((p) => ({ ...p, firstName: true }))}
                />
                {showAccountFieldError("firstName") ? (
                  <small className="reg-field-error">{showAccountFieldError("firstName")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>Last Name</RegReqLabel>
                <input
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  onBlur={() => setAccountBlurred((p) => ({ ...p, lastName: true }))}
                />
                {showAccountFieldError("lastName") ? (
                  <small className="reg-field-error">{showAccountFieldError("lastName")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>Phone</RegReqLabel>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", formatUsPhoneTyping(e.target.value))}
                  onBlur={() => setAccountBlurred((p) => ({ ...p, phone: true }))}
                />
                {showAccountFieldError("phone") ? (
                  <small className="reg-field-error">{showAccountFieldError("phone")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>Email</RegReqLabel>
                <input
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onBlur={() => setAccountBlurred((p) => ({ ...p, email: true }))}
                />
                {showAccountFieldError("email") ? (
                  <small className="reg-field-error">{showAccountFieldError("email")}</small>
                ) : null}
              </label>
              {!skipsPasswordOnAccount ? (
                <>
                  <RegPasswordField
                    label="Password"
                    required
                    value={form.password}
                    onChange={(v) => setField("password", v)}
                    onBlur={() => setAccountBlurred((p) => ({ ...p, password: true }))}
                    error={showAccountFieldError("password")}
                  />
                  <RegPasswordField
                    label="Confirm Password"
                    required
                    value={form.passwordConfirm}
                    matchesValue={form.password}
                    onChange={(v) => setField("passwordConfirm", v)}
                    onBlur={() => setAccountBlurred((p) => ({ ...p, passwordConfirm: true }))}
                    error={showAccountFieldError("passwordConfirm")}
                  />
                </>
              ) : null}
            </div>
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button
                type="button"
                className="reg-btn subtle"
                onClick={() => {
                  setSubmitError(null);
                  if (hasQuickCode) {
                    setQuickCode("");
                    setOmitPasswordAfterQc(false);
                    setQcResumeEmailSnapshot(null);
                    setForm({ ...DEFAULT_VALUES });
                    setExtendedForm(registrationExtendedDefaults());
                    changeStep("quickCodeEntry", "backward");
                  } else {
                    changeStep("quickCode", "backward");
                  }
                }}
              >
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedAccount || basicsSubmitting}
                onClick={() => void submitAccountBasics()}
              >
                {basicsSubmitting ? "Saving…" : "Next"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "jurisdictions" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">Confirm your Jurisdiction</h2>
            <p className="reg-step-lead reg-jurisdiction-count">
              <strong>Current Jurisdictions: {extendedForm.jurisdictions.length}</strong>
              <span> (Max of 5)</span>
            </p>
            <div className="reg-jurisdiction-list">
              {extendedForm.jurisdictions.map((row, index) => {
                const states = listRegistrationStatesForCountry(row.country);
                const counties = listRegistrationCountiesForState(row.country, row.state);
                return (
                  <div key={index} className="reg-jurisdiction-block">
                    <div className="reg-jurisdiction-block-head">
                      <span className="reg-jurisdiction-label">Jurisdiction {index + 1}</span>
                      {extendedForm.jurisdictions.length > 1 ? (
                        <button type="button" className="reg-btn-link" onClick={() => removeJurisdictionRow(index)}>
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="reg-grid">
                      <label className="reg-field">
                        <RegReqLabel required>Country</RegReqLabel>
                        <select
                          value={row.country}
                          onChange={(e) =>
                            patchJurisdiction(index, { country: e.target.value, state: "", county: "" })
                          }
                          onBlur={() =>
                            setJurisdictionCellBlurred((p) => ({ ...p, [`${index}-country`]: true }))
                          }
                        >
                          {row.country && !REGISTRATION_COUNTRIES.includes(row.country) ? (
                            <option value={row.country}>{row.country}</option>
                          ) : null}
                          {REGISTRATION_COUNTRIES.map((c: string) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {showJurisdictionCellError(index, "country") ? (
                          <small className="reg-field-error">{showJurisdictionCellError(index, "country")}</small>
                        ) : null}
                      </label>
                      <label className="reg-field">
                        <RegReqLabel required>State / Province</RegReqLabel>
                        <select
                          value={row.state}
                          onChange={(e) => patchJurisdiction(index, { state: e.target.value, county: "" })}
                          onBlur={() =>
                            setJurisdictionCellBlurred((p) => ({ ...p, [`${index}-state`]: true }))
                          }
                        >
                          <option value="">Select</option>
                          {row.state && !states.includes(row.state) ? (
                            <option value={row.state}>{row.state}</option>
                          ) : null}
                          {states.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {showJurisdictionCellError(index, "state") ? (
                          <small className="reg-field-error">{showJurisdictionCellError(index, "state")}</small>
                        ) : null}
                      </label>
                      <label className="reg-field">
                        <RegReqLabel required>County</RegReqLabel>
                        <select
                          value={row.county}
                          onChange={(e) => patchJurisdiction(index, { county: e.target.value })}
                          onBlur={() =>
                            setJurisdictionCellBlurred((p) => ({ ...p, [`${index}-county`]: true }))
                          }
                        >
                          <option value="">Select</option>
                          {row.county && !counties.includes(row.county) ? (
                            <option value={row.county}>{row.county}</option>
                          ) : null}
                          {counties.map((co) => (
                            <option key={co} value={co}>
                              {co}
                            </option>
                          ))}
                        </select>
                        {showJurisdictionCellError(index, "county") ? (
                          <small className="reg-field-error">{showJurisdictionCellError(index, "county")}</small>
                        ) : null}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            {extendedForm.jurisdictions.length < 5 ? (
              <div className="reg-jurisdiction-add-wrap">
                <button
                  type="button"
                  className="reg-btn subtle reg-btn-add-jurisdiction"
                  disabled={!firstJurisdictionComplete}
                  onClick={addJurisdictionRow}
                >
                  Add Jurisdiction
                </button>
              </div>
            ) : null}
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedJurisdictions || busy}
                onClick={advanceProfileStep}
              >
                {step === "jurisdictions" ? profileStepNextLabel() : "Next"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "mailing" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">
              {isErLike ? "Agency Address" : "Mailing Address"}
            </h2>
            <div className="reg-grid">
              <label className="reg-field">
                <RegReqLabel required>Address Line 1</RegReqLabel>
                <input
                  value={extendedForm.mailingAddress1}
                  onChange={(e) => setExt("mailingAddress1", e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingAddress1: true }))}
                />
                {showMailingFieldError("mailingAddress1") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingAddress1")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <span>Address Line 2</span>
                <input
                  value={extendedForm.mailingAddress2}
                  onChange={(e) => setExt("mailingAddress2", e.target.value)}
                />
              </label>
              <label className="reg-field">
                <RegReqLabel required>City</RegReqLabel>
                <input
                  value={extendedForm.mailingCity}
                  onChange={(e) => setExt("mailingCity", e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingCity: true }))}
                />
                {showMailingFieldError("mailingCity") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingCity")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>Country</RegReqLabel>
                <select
                  value={extendedForm.mailingCountry}
                  onChange={(e) => setMailingCountry(e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingCountry: true }))}
                >
                  {extendedForm.mailingCountry && !REGISTRATION_COUNTRIES.includes(extendedForm.mailingCountry) ? (
                    <option value={extendedForm.mailingCountry}>{extendedForm.mailingCountry}</option>
                  ) : null}
                  {REGISTRATION_COUNTRIES.map((c: string) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {showMailingFieldError("mailingCountry") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingCountry")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>State / Province</RegReqLabel>
                <select
                  value={extendedForm.mailingState}
                  onChange={(e) => setMailingState(e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingState: true }))}
                >
                  <option value="">Select</option>
                  {extendedForm.mailingState && !mailingStateOptions.includes(extendedForm.mailingState) ? (
                    <option value={extendedForm.mailingState}>{extendedForm.mailingState}</option>
                  ) : null}
                  {mailingStateOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {showMailingFieldError("mailingState") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingState")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>County</RegReqLabel>
                <select
                  value={extendedForm.mailingCounty}
                  onChange={(e) => setExt("mailingCounty", e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingCounty: true }))}
                >
                  <option value="">Select</option>
                  {extendedForm.mailingCounty && !mailingCountyOptions.includes(extendedForm.mailingCounty) ? (
                    <option value={extendedForm.mailingCounty}>{extendedForm.mailingCounty}</option>
                  ) : null}
                  {mailingCountyOptions.map((co) => (
                    <option key={co} value={co}>
                      {co}
                    </option>
                  ))}
                </select>
                {showMailingFieldError("mailingCounty") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingCounty")}</small>
                ) : null}
              </label>
              <label className="reg-field">
                <RegReqLabel required>Zip / Postal Code</RegReqLabel>
                <input
                  value={extendedForm.mailingZip}
                  onChange={(e) => setExt("mailingZip", e.target.value)}
                  onBlur={() => setMailingBlurred((p) => ({ ...p, mailingZip: true }))}
                />
                {showMailingFieldError("mailingZip") ? (
                  <small className="reg-field-error">{showMailingFieldError("mailingZip")}</small>
                ) : null}
              </label>
            </div>
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedMailing || crmSubmitting}
                onClick={advanceProfileStep}
              >
                {step === "mailing" ? profileStepNextLabel() : "Next"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "organization" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">Organization</h2>
            <div className="reg-grid">
              <label className="reg-field">
                <RegReqLabel required>Organization Name</RegReqLabel>
                <input
                  value={extendedForm.institution}
                  onChange={(e) => setExt("institution", e.target.value)}
                  onBlur={() => setOrgBlurred((p) => ({ ...p, institution: true }))}
                />
                {showOrgFieldError("institution") ? (
                  <small className="reg-field-error">{showOrgFieldError("institution")}</small>
                ) : null}
              </label>
              {showOrganizationTypeField(wizardStakeholderType) ? (
                <label className="reg-field">
                  <RegReqLabel required>Organization Type</RegReqLabel>
                  <select
                    value={extendedForm.organizationType}
                    onChange={(e) => setExt("organizationType", e.target.value)}
                    onBlur={() => setOrgBlurred((p) => ({ ...p, organizationType: true }))}
                  >
                    <option value="">Select</option>
                    {(form.stakeholderType === STAKEHOLDER_SCHOOL_REP
                      ? SCHOOL_REP_ORG_TYPES
                      : PUBLIC_OFFICIAL_ORG_TYPES
                    ).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {showOrgFieldError("organizationType") ? (
                    <small className="reg-field-error">{showOrgFieldError("organizationType")}</small>
                  ) : null}
                </label>
              ) : null}
              {showOrganizationDepartmentField(wizardStakeholderType, form.jobTitle) ? (
                <label className="reg-field">
                  <RegReqLabel required>Department</RegReqLabel>
                  <input
                    value={extendedForm.department}
                    onChange={(e) => setExt("department", e.target.value)}
                    onBlur={() => setOrgBlurred((p) => ({ ...p, department: true }))}
                  />
                  {showOrgFieldError("department") ? (
                    <small className="reg-field-error">{showOrgFieldError("department")}</small>
                  ) : null}
                </label>
              ) : null}
              <label className="reg-field">
                <RegReqLabel required>Organization Phone</RegReqLabel>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={extendedForm.institutionPhone}
                  onChange={(e) => setExt("institutionPhone", formatUsPhoneTyping(e.target.value))}
                  onBlur={() => setOrgBlurred((p) => ({ ...p, institutionPhone: true }))}
                />
                {showOrgFieldError("institutionPhone") ? (
                  <small className="reg-field-error">{showOrgFieldError("institutionPhone")}</small>
                ) : null}
              </label>
            </div>
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedOrganization || crmSubmitting}
                onClick={advanceProfileStep}
              >
                {step === "organization" ? profileStepNextLabel() : "Next"}
              </button>
            </div>
          </section>
        ) : null}

        {step === "agencyInfo" ? (
          <section className="reg-card">
            <AgencyInfoStep
              extendedForm={extendedForm}
              stakeholderType={wizardStakeholderType}
              showFieldError={showAgencyFieldError}
              onPatch={patchExtended}
              onBlur={(field) => setAgencyBlurred((p) => ({ ...p, [field]: true }))}
            />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedAgencyInfo || busy}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "agencyCapability" ? (
          <section className="reg-card">
            <AgencyCapabilitiesStep extendedForm={extendedForm} onPatch={patchExtended} />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={busy}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "agencyProcedure" ? (
          <section className="reg-card">
            <AgencyProceduresStep extendedForm={extendedForm} onPatch={patchExtended} />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={busy}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "agencyTraining" ? (
          <section className="reg-card">
            <AgencyTrainingStep
              extendedForm={extendedForm}
              hazwoperError={hazwoperError}
              showHazwoperError={trainingSubmitAttempted || hazwoperBlurred}
              onPatch={patchExtended}
              onHazwoperBlur={() => setHazwoperBlurred(true)}
            />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedAgencyTraining || busy}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "dispatchCapabilities" ? (
          <section className="reg-card">
            <DispatchCapabilitiesStep
              part={dispatchPart}
              extendedForm={extendedForm}
              showFieldError={showDispatchFieldError}
              onPatch={patchExtended}
              onBlur={(field) => setDispatchBlurred((p) => ({ ...p, [field]: true }))}
            />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackDispatchPart}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={!canProceedDispatchPart || busy}
                onClick={advanceDispatchPart}
              >
                {dispatchPart < DISPATCH_CAPABILITIES_PART_COUNT ? "Next" : profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "agencyAgreement" ? (
          <section className="reg-card">
            <MutualAidStep extendedForm={extendedForm} onPatch={patchExtended} />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={crmSubmitting}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "additionalQuestions" ? (
          <section className="reg-card">
            <AdditionalQuestionsStep
              extendedForm={extendedForm}
              showFieldError={showAdditionalQuestionsFieldError}
              onPatch={patchExtended}
              onBlur={(field) =>
                setAdditionalQuestionsBlurred((p) => ({ ...p, [field]: true }))
              }
            />
            {submitError ? <p className="reg-error">{submitError}</p> : null}
            <div className="reg-actions">
              <button type="button" className="reg-btn subtle" onClick={goBackProfileStep}>
                Back
              </button>
              <button type="button" className="reg-btn subtle" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="reg-btn primary"
                disabled={crmSubmitting}
                onClick={advanceProfileStep}
              >
                {profileStepNextLabel()}
              </button>
            </div>
          </section>
        ) : null}

        {step === "done" ? (
          <section className="reg-card">
            <h2 className="reg-step-title">Successfully Registered to Buxus®</h2>
            {successParagraphs.map((paragraph) => (
              <p key={paragraph} className="reg-step-lead">
                {paragraph}
              </p>
            ))}
            <div className="reg-actions">
              <button
                type="button"
                className="reg-btn primary"
                onClick={() => {
                  if (hasRegistrationNativeBridge()) {
                    postRegistrationCompleteToLogin();
                    return;
                  }
                  navigate("/", { replace: true });
                }}
              >
                Continue
              </button>
            </div>
          </section>
        ) : null}
    </RegistrationShell>
  );
}
